import { Dispatch, SetStateAction, useState } from 'react';

import { Button } from '~/modules/elements/akui/button';
import { Icon } from '~/modules/elements/akui/icon';
import useBreakpoint from '~/modules/hooks/use-breakpoint';
import { RegisterFunc } from '~/modules/hooks/use-keyboard-nav';
import SearchBar from '~/routes/sing-a-song/song-selection/components/toolbar/search-bar';
import { AppliedFilters } from '~/routes/sing-a-song/song-selection/hooks/use-song-list-filter';

interface ToolbarProps {
  filters: AppliedFilters;
  setFilters: Dispatch<SetStateAction<AppliedFilters>>;
  onRandom: () => void;
  onOpenFilters: () => void;
  /** Whether anything in the filter panel is on - the dot on the icon is the only thing that says so. */
  filtersActive: boolean;
  keyboardControl: boolean;
  keyboardNavRegister?: RegisterFunc;
  /** True while keyboard nav is active inside the toolbar — suppresses hotkeys that would interfere. */
  toolbarNavActive?: boolean;
}

export default function Toolbar({
  filters,
  setFilters,
  onRandom,
  onOpenFilters,
  filtersActive,
  keyboardControl,
  keyboardNavRegister,
  toolbarNavActive = false,
}: ToolbarProps) {
  const breakpoint = useBreakpoint();
  // Search collapses to an icon only when the song list shows 1 card per row (< 640px)
  const collapseSearch = breakpoint === 'xs';

  // Set synchronously by SearchBar (via useLayoutEffect) when it enters/exits xs-expanded mode,
  // so the random and filter buttons are hidden in the same paint frame.
  const [searchExpanded, setSearchExpanded] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <SearchBar
        filters={filters}
        setFilters={setFilters}
        keyboardControl={keyboardControl}
        keyboardNavRegister={keyboardNavRegister}
        toolbarNavActive={toolbarNavActive}
        collapseSearch={collapseSearch}
        onExpandedChange={setSearchExpanded}
      />

      {/* Hidden when search is in xs-expanded mode — SearchBar fills the full width */}
      {!searchExpanded && (
        <>
          <Button
            size={{ xs: 'mini', sm: 'small' }}
            type="button"
            aria-label="Random song"
            data-test="random-song-button"
            className="shrink-0 animate-none"
            {...keyboardNavRegister?.('random-song-button', onRandom, 'Random song')}
            leftIcon={<Icon icon="ic:baseline-casino" />}
            fullWidth={false}
            onClick={onRandom}
          />

          <div className="relative shrink-0">
            <Button
              size={{ xs: 'mini', sm: 'small' }}
              type="button"
              aria-label="Filters"
              data-test="filters-button"
              className="animate-none"
              {...keyboardNavRegister?.('filters-button', onOpenFilters, 'Filters')}
              leftIcon={<Icon icon="ic:baseline-filter-list" />}
              fullWidth={false}
              onClick={onOpenFilters}
            />
            {filtersActive && (
              <span
                data-test="filters-active-dot"
                aria-hidden="true"
                className="bg-active pointer-events-none absolute top-[0.4375rem] right-[0.4375rem] size-[0.5625rem] rounded-full shadow-[0_0_0_2px_rgba(0,0,0,.75)]"
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
